import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Trash2, TrendingUp, TrendingDown, PiggyBank, Pencil, X, Check } from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { API } from '../utils/api';

const EXPENSE_CATEGORIES = [
  "Housing",
  "Utilities",
  "Groceries",
  "Dining",
  "Transportation",
  "Healthcare",
  "Insurance",
  "Entertainment",
  "Shopping",
  "Education",
  "Subscriptions",
  "Personal Care",
  "Childcare",
  "Other",
];

const INCOME_CATEGORIES = [
  "Salary",
  "Side Income",
  "Freelance",
  "Investment Returns",
  "Rental Income",
  "Other Income",
];

const SAVINGS_CATEGORIES = [
  "Investment",
  "Savings",
];

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  }).format(value);
};

export default function MonthlyTracking({ user }) {
  const [data, setData] = useState({ items: [], total_income: 0, total_expenses: 0, total_savings: 0, net: 0 });
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    category: "",
    description: "",
    amount: "",
    item_type: "expense",
  });

  useEffect(() => {
    fetchBudget();
  }, []);

  const fetchBudget = async () => {
    try {
      const response = await axios.get(`${API}/monthly-budget`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching budget:", error);
      toast.error("Failed to load monthly budget");
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.category || !newItem.amount) {
      toast.error("Please fill in category and amount");
      return;
    }

    try {
      await axios.post(`${API}/monthly-budget`, {
        description: newItem.description || newItem.category,
        amount: parseFloat(newItem.amount),
        item_type: newItem.item_type,
        category: newItem.category,
      });
      
      toast.success("Item added");
      setNewItem({ category: "", description: "", amount: "", item_type: "expense" });
      fetchBudget();
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/monthly-budget/${itemId}`);
      toast.success("Item deleted");
      fetchBudget();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({
      ...item,
      amount: item.amount.toString(),
    });
  };

  const handleUpdateItem = async () => {
    if (!editingItem.category || !editingItem.amount) {
      toast.error("Please fill in category and amount");
      return;
    }

    try {
      await axios.put(`${API}/monthly-budget/${editingItem.item_id}`, {
        description: editingItem.description || editingItem.category,
        amount: parseFloat(editingItem.amount),
        item_type: editingItem.item_type,
        category: editingItem.category,
      });
      
      toast.success("Item updated");
      setEditingItem(null);
      fetchBudget();
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update item");
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
  };

  const currency = user?.default_currency || "EUR";
  
  // Get categories based on item type
  const getCategories = (itemType) => {
    if (itemType === "income") return INCOME_CATEGORIES;
    if (itemType === "savings") return SAVINGS_CATEGORIES;
    return EXPENSE_CATEGORIES;
  };

  const incomeItems = data.items.filter(item => item.item_type === "income");
  const expenseItems = data.items.filter(item => item.item_type === "expense");
  const savingsItems = data.items.filter(item => item.item_type === "savings");

  if (loading) {
    return (
      <Layout user={user} pageTitle="Monthly Budget">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Monthly Budget">
      <div data-testid="monthly-tracking-page" className="space-y-8">
        {/* Header */}
        <div>
          <p className="text-gray-400">
            Define your typical monthly income, expenses, and savings/investments. Monthly Savings will be used in Future Planning.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Monthly Income</p>
                  <p className="text-2xl font-bold text-green-400 number-display">
                    {formatCurrency(data.total_income, currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Monthly Expenses</p>
                  <p className="text-2xl font-bold text-red-400 number-display">
                    {formatCurrency(data.total_expenses, currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                  <PiggyBank className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Monthly Savings</p>
                  <p className="text-2xl font-bold text-blue-400 number-display">
                    {formatCurrency(data.total_savings, currency)}
                  </p>
                  <p className="text-xs text-gray-500">→ Future Planning</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Net Balance</p>
                  <p className={`text-2xl font-bold number-display ${data.net >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                    {formatCurrency(data.net, currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add New Item */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-white">Add Budget Item</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Select
                value={newItem.item_type}
                onValueChange={(value) => setNewItem({ ...newItem, item_type: value, category: "" })}
              >
                <SelectTrigger className="bg-secondary border-border text-white">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="income" className="text-gray-300 focus:text-white focus:bg-secondary">Income</SelectItem>
                  <SelectItem value="expense" className="text-gray-300 focus:text-white focus:bg-secondary">Expense</SelectItem>
                  <SelectItem value="savings" className="text-gray-300 focus:text-white focus:bg-secondary">Savings/Investment</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={newItem.category}
                onValueChange={(value) => setNewItem({ ...newItem, category: value })}
              >
                <SelectTrigger className="bg-secondary border-border text-white">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {getCategories(newItem.item_type).map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-gray-300 focus:text-white focus:bg-secondary">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder="Description (optional)"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="bg-secondary border-border text-white placeholder:text-gray-500"
              />

              <Input
                type="number"
                placeholder={`Amount (${currency})`}
                value={newItem.amount}
                onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                className="bg-secondary border-border text-white placeholder:text-gray-500"
                data-testid="amount-input"
              />

              <Button
                onClick={handleAddItem}
                className="bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Income and Expenses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Monthly Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              {incomeItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No income items yet</p>
              ) : (
                <div className="space-y-3">
                  {incomeItems.map((item) => (
                    <motion.div
                      key={item.item_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between p-3 bg-secondary rounded-xl"
                    >
                      {editingItem?.item_id === item.item_id ? (
                        <>
                          <div className="flex-1 grid grid-cols-3 gap-2 mr-2">
                            <Input
                              value={editingItem.description}
                              onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                              placeholder="Description"
                              className="bg-background border-border text-white text-sm"
                            />
                            <Select
                              value={editingItem.category}
                              onValueChange={(value) => setEditingItem({ ...editingItem, category: value })}
                            >
                              <SelectTrigger className="bg-background border-border text-white text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {INCOME_CATEGORIES.map((cat) => (
                                  <SelectItem key={cat} value={cat} className="text-gray-300 focus:text-white focus:bg-secondary">
                                    {cat}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              value={editingItem.amount}
                              onChange={(e) => setEditingItem({ ...editingItem, amount: e.target.value })}
                              className="bg-background border-border text-white text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-500 hover:text-green-400"
                              onClick={handleUpdateItem}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-gray-400"
                              onClick={cancelEdit}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="font-medium text-white">{item.description || item.category}</p>
                            <p className="text-sm text-gray-500">{item.category}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-green-400 number-display">
                              +{formatCurrency(item.amount, currency)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-blue-400"
                              onClick={() => handleEditItem(item)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-red-400"
                              onClick={() => handleDeleteItem(item.item_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expenses */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Monthly Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expenseItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No expense items yet</p>
              ) : (
                <div className="space-y-3">
                  {expenseItems.map((item) => (
                    <motion.div
                      key={item.item_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between p-3 bg-secondary rounded-xl"
                    >
                      {editingItem?.item_id === item.item_id ? (
                        <>
                          <div className="flex-1 grid grid-cols-3 gap-2 mr-2">
                            <Input
                              value={editingItem.description}
                              onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                              placeholder="Description"
                              className="bg-background border-border text-white text-sm"
                            />
                            <Select
                              value={editingItem.category}
                              onValueChange={(value) => setEditingItem({ ...editingItem, category: value })}
                            >
                              <SelectTrigger className="bg-background border-border text-white text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {EXPENSE_CATEGORIES.map((cat) => (
                                  <SelectItem key={cat} value={cat} className="text-gray-300 focus:text-white focus:bg-secondary">
                                    {cat}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              value={editingItem.amount}
                              onChange={(e) => setEditingItem({ ...editingItem, amount: e.target.value })}
                              className="bg-background border-border text-white text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-500 hover:text-green-400"
                              onClick={handleUpdateItem}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-gray-400"
                              onClick={cancelEdit}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <p className="font-medium text-white">{item.description || item.category}</p>
                            <p className="text-sm text-gray-500">{item.category}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-red-400 number-display">
                              -{formatCurrency(item.amount, currency)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-blue-400"
                              onClick={() => handleEditItem(item)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-red-400"
                              onClick={() => handleDeleteItem(item.item_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Savings & Investments */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-blue-400" />
              Monthly Savings & Investments
              <span className="text-sm font-normal text-gray-500 ml-2">
                (Used in Future Planning)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {savingsItems.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No savings/investment items yet. Add items with type Savings/Investment.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savingsItems.map((item) => (
                  <motion.div
                    key={item.item_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-4 bg-secondary rounded-xl border border-blue-500/20"
                  >
                    {editingItem?.item_id === item.item_id ? (
                      <div className="flex-1">
                        <div className="space-y-2">
                          <Input
                            value={editingItem.description}
                            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                            placeholder="Description"
                            className="bg-background border-border text-white text-sm"
                          />
                          <Select
                            value={editingItem.category}
                            onValueChange={(value) => setEditingItem({ ...editingItem, category: value })}
                          >
                            <SelectTrigger className="bg-background border-border text-white text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {SAVINGS_CATEGORIES.map((cat) => (
                                <SelectItem key={cat} value={cat} className="text-gray-300 focus:text-white focus:bg-secondary">
                                  {cat}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={editingItem.amount}
                            onChange={(e) => setEditingItem({ ...editingItem, amount: e.target.value })}
                            className="bg-background border-border text-white text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-1 mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-500 hover:text-green-400"
                            onClick={handleUpdateItem}
                          >
                            <Check className="w-4 h-4 mr-1" /> Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-gray-400"
                            onClick={cancelEdit}
                          >
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <p className="font-medium text-white">{item.description || item.category}</p>
                          <p className="text-sm text-gray-500">{item.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-blue-400 number-display">
                            {formatCurrency(item.amount, currency)}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-500 hover:text-blue-400"
                            onClick={() => handleEditItem(item)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-500 hover:text-red-400"
                            onClick={() => handleDeleteItem(item.item_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
